import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Logo } from '@/ui/components/logo';
import { Card } from '@/ui/components/card';
import { useState } from 'react';
import { useAuth } from '@/features/auth';
import { safePostLoginHref } from '@/features/auth/utils';
import * as z from 'zod';
import { Field, FieldControl, FieldError, FieldLabel } from '@/ui/components/form/field';
import { Form } from '@/ui/components/form';
import { Button } from '@/ui/components/button';
import { tryCatch } from '@/try-catch';
import { LoadingState } from '@/ui/components/loading-state';

export const Route = createFileRoute('/_auth/login')({
  component: RouteComponent,
  validateSearch: z.object({
    email: z.string().optional(),
    target: z.string().optional(),
    activationSuccess: z.boolean().optional(),
    inviteAccepted: z.boolean().optional(),
    organizationName: z.string().optional(),
    success: z.union([z.boolean(), z.enum(['true', 'false'])]).optional(),
    message: z.string().optional(),
  }),
});

function RouteComponent() {
  const search = Route.useSearch();
  const { isAuthenticated, isLoading, login } = useAuth();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const appState = {
    returnTo: safePostLoginHref(search.target),
    activationSuccess:
      search.activationSuccess || search.success === true || search.success === 'true',
  };

  if (isLoading) return <LoadingState context="auth" />;

  if (isAuthenticated) return <Navigate to="/" />;

  return (
    <div className="px-5 -translate-y-5 w-full">
      <Logo className="mb-5 mx-auto" />

      <Card className="max-w-92 lg:p-6 p-5 mx-auto w-full">
        <h1 className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
          {t({ en: 'Log in to your workspace', nb: 'Logg på din konto' })}
        </h1>

        <p className="text-center text-muted text-sm mb-4">
          {t({ en: 'Enter your email to continue', nb: 'Skriv inn din e-post for å fortsette' })}
        </p>

        <Form
          onFormSubmit={async (formValues) => {
            setIsFormLoading(true);
            setLoginError('');
            const { error } = await tryCatch(() =>
              login({
                appState,
                loginHint: (formValues.email as string).replace(/\+/g, '%2B'),
              }),
            );
            if (error) setLoginError(t(common.somethingWentWrong));
            setIsFormLoading(false);
          }}
          className="space-y-6"
        >
          <Field name="email">
            <FieldLabel>{t({ en: 'Email', nb: 'E-post' })}</FieldLabel>
            <FieldControl
              autoFocus
              type="email"
              required
              defaultValue={decodeURIComponent(search.email ?? '')}
              placeholder={t({ en: 'you@example.com', nb: 'du@eksempel.com' })}
            />
            <FieldError match="valueMissing">
              {t({ en: 'Email is required', nb: 'E-post er påkrevd' })}
            </FieldError>
            <FieldError match="typeMismatch">
              {t({ en: 'Invalid email address', nb: 'Ugyldig e-post' })}
            </FieldError>
          </Field>

          {loginError && <p className="text-destructive text-sm text-center">{loginError}</p>}

          <Button size="lg" type="submit" pending={isFormLoading} className="w-full">
            {t({ en: 'Continue', nb: 'Fortsett' })}
          </Button>
        </Form>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-muted text-xs">{t(common.or)}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          size="lg"
          variant="secondary"
          className="w-full"
          onClick={() => login({ connection: import.meta.env.VITE_AUTH0_SSO_CONNECTION, appState })}
        >
          {t({ en: 'Sign in with SSO', nb: 'Logg inn med SSO' })}
        </Button>
      </Card>
    </div>
  );
}
